import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { resolveRouteMeta } from "@/seo/routeMeta";
import Home from "@/pages/Home";
import HomeV2 from "@/pages/HomeV2";
import StrategiesPage from "@/pages/StrategiesPage";
import StrategyDetailPage from "@/pages/StrategyDetailPage";
import FundsPage from "@/pages/FundsPage";
import CapitalPathwaysPage from "@/pages/CapitalPathwaysPage";
import TeamPage from "@/pages/TeamPage";
import ContactPage from "@/pages/ContactPage";
import ResponsibleCapitalPage from "@/pages/ResponsibleCapitalPage";
import NewsPage from "@/pages/NewsPage";
import NewsDetailPage from "@/pages/NewsDetailPage";
import RegionalCoveragePage from "@/pages/RegionalCoveragePage";
import GulfInvestorsPage from "@/pages/GulfInvestorsPage";
import FamilyOfficePage from "@/pages/FamilyOfficePage";
import PerspectivesPage from "@/pages/PerspectivesPage";
import PerspectiveDetailPage from "@/pages/PerspectiveDetailPage";
import PressKitPage from "@/pages/PressKitPage";
import CareersPage from "@/pages/CareersPage";
import DisclosuresPage from "@/pages/DisclosuresPage";
import PartnershipPage from "@/pages/PartnershipPage";
import AdvisorsPage from "@/pages/AdvisorsPage";
import LeadershipPage from "@/pages/LeadershipPage";
import { LegalPage, PrivacyPage, CookiesPage } from "@/pages/LegalPages";
import NotFoundPage from "@/pages/NotFoundPage";
import CookieBanner from "@/components/landing/CookieBanner";

/**
 * When navigating with hash links across routes (e.g. /#contact),
 * react-router does not auto-scroll. This listens for hash changes
 * after route transitions and smoothly scrolls into view — retrying
 * briefly because target sections may not be mounted on the new
 * route at the moment the effect first fires.
 */
const HashScroller = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (attempts++ < 20) {
          setTimeout(tryScroll, 80);
        }
      };
      tryScroll();
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);
  return null;
};

const RouteMeta = () => {
  const { pathname } = useLocation();
  usePageMeta(resolveRouteMeta(pathname));
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <HashScroller />
        <RouteMeta />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/v2" element={<HomeV2 />} />
          <Route path="/strategies" element={<StrategiesPage />} />
          <Route path="/strategies/:slug" element={<StrategyDetailPage />} />
          <Route path="/funds" element={<FundsPage />} />
          <Route path="/capital-pathways" element={<CapitalPathwaysPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/responsible-capital" element={<ResponsibleCapitalPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:slug" element={<NewsDetailPage />} />
          <Route path="/perspectives" element={<PerspectivesPage />} />
          <Route path="/perspectives/:slug" element={<PerspectiveDetailPage />} />
          <Route path="/press-kit" element={<PressKitPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/disclosures" element={<DisclosuresPage />} />
          <Route path="/partnership" element={<PartnershipPage />} />
          <Route path="/advisors" element={<AdvisorsPage />} />
          <Route path="/leadership" element={<LeadershipPage />} />
          <Route path="/regional-coverage" element={<RegionalCoveragePage />} />
          <Route path="/gulf-investors" element={<GulfInvestorsPage />} />
          <Route
            path="/family-office-investment-solutions"
            element={<FamilyOfficePage />}
          />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
    </div>
  );
}

export default App;
